import { HTMLProps, useEffect, useRef, useState } from "react";

import { ArchiveBoxIcon, CheckCircleIcon, EyeIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useParts from "../../data/Parts/useParts";
import { IEbayPart } from "../../models/EbayPart/EbayPart";
import { Table } from "../../components/base/Table/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import PartsCategoryModal from "../../components/Scrape/PartsCategoryModal";
import { PartQueryKeys } from "../../data/Category/keys";
import CategorySelect from "../../components/Scrape/CategorySelect";
import { useForm } from "react-hook-form";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { Field, Label, Switch } from "@headlessui/react";
import classNames from "../../utils/classNames";
import { ImagePreviewCell } from "../../components/shared/ImagePreviewCell";
import { CategorySearchTermSelect } from "../../components/Scrape/CategorySearchTermSelect";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { ICategorySearchTerm } from "../../models/Category/Category";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "../../layouts/Modal";


function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = useRef<HTMLInputElement>(null!)

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate
        }
    }, [ref, indeterminate])

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + 'p-3 cursor-pointer w-6 h-6'}
            {...rest}
        />
    )
}

export default function Parts() {
    const { saveHandlers } = useDefaultCRUDHandlers("parts");
    const { register, setValue, getValues, handleSubmit, watch} = useForm<any>();

    const { archive, acceptCategory, updatePart, assignCategory, totals: allTotals } = useParts({ limit: undefined });
    
    const { setInfo } = usePageNameContext();
    const navigate = useNavigate();
    const [part, setPart] = useState<IEbayPart | undefined>(undefined);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [toArchive, setToArchive] = useState<string[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);

    const [selectedParts, setSelectedParts] = useState<IEbayPart[]>([]);
    const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);

    const [hideCategorised, setHideCategorised] = useState(true);
    const [hideAccepted, setHideAccepted] = useState(true);
    const [include, setInclude] = useState<string>("");
    const [exclude, setExclude] = useState<string>("");
    const [externalCategory, setExternalCategory] = useState<string>("");
    const [categorySearch, setCategorySearch] = useState<string>("");
    const [saveSearchTerm, setSaveSearchTerm] = useState<boolean>(false);
    const [metadataOpen, setMetadataOpen] = useState(false);
    const [metadataPart, setMetadataPart] = useState<IEbayPart | null>(null);

    const [selectedSearchTerm, setSelectedSearchTerm] = useState<ICategorySearchTerm | null>(null);
    
    const queryClient = useQueryClient();
    
    const colHelper = createColumnHelper<IEbayPart>();

    const handleArchive = () => {
        archive.mutate({parts: toArchive}, {
            onSuccess: () => {
                console.log("Part archived successfully");
                saveHandlers.onSuccess();
                setArchiveOpen(false);
            },
            onError: (error) => {
                console.error("Error archiving part:", error);
            }
        });
    }

    const handleSave = (part: IEbayPart) => {
        const rowData = {
            partId: part.id,
            categoryId: getValues(`category-${part.id}`)?.id || undefined,
            partNumber: getValues(`partNumber-${part.id}`),
            categoryAccepted: getValues(`category-${part.id}`)?.id ? 1 : 0,

            saveSearchTerm: saveSearchTerm,
            include: include,
            exclude: exclude,
        }

        console.log("Saving part data:", rowData);

        updatePart.mutate(rowData, {
            onSuccess: () => {
                console.log("Part updated successfully");
                saveHandlers.onSuccess();
            },
            onError: (error) => {
                console.error("Error updating part:", error);
            }
        });
    }

    useEffect(() => {
        setInfo({
            name: "Part Editor",
            desc: "View and edit your parts here",
            pageTitle: "Parts",
        });
    }, []);

    const cols = [
        {
            id: 'select',
            header: ({ table }: any) => {
                return (
                    <IndeterminateCheckbox
                        {...{
                            checked: table.getIsAllRowsSelected(),
                            indeterminate: table.getIsSomeRowsSelected(),
                            onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                )
            },
            cell: ({ row }: any) => (
                <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                </div>
            ),
        },
        colHelper.display({
            id: "image",
            cell: (info) => (
                <ImagePreviewCell urls={info.row.original.urls} />
            ),
        }),
        // colHelper.accessor("title", { header: "Title" }),
        colHelper.accessor("title", {
            header: "Title",
            cell: (info) => (
                <div className="max-w-xl text-wrap truncate" title={info.getValue()}>
                    {info.getValue()}
                </div>
            )
        }),
        colHelper.accessor("itemSpecifics", {
            header: "Item Specifics",
            cell: (info) => {
                return (
                    <div className="max-w-48 text-nowrap truncate" title={info.getValue()}>
                        {info.getValue()}
                    </div>
                )
            }
        }),
        colHelper.accessor("quickspecs", {
            header: "Quick Specs",
            cell: (info) => (
                <div className="max-w-48 text-nowrap truncate" title={info.getValue()}>
                    {info.getValue()}
                </div>
            )
        }),
        colHelper.accessor("price", {
            header: "Price",
            cell: (info) => (
                <div className="max-w-xl text-wrap">
                    £{info.getValue()}
                </div>
            )
        }),
        colHelper.display({
            header: "Part #",
            id: "partNumber",
            cell: (info) => {
                setValue(`partNumber-${info.row.original.id}`, info.row.original.partNumber || "");
                return (<span className="flex justify-center relative min-w-48">
                    <input
                        {...register(`partNumber-${info.row.original.id}`)}
                        type="text"
                        className="rounded border outline-none focus:drop-shadow-none focus:ring-2 !border-orange-600 focus:ring-orange-600 px-3 py-2"
                    />
                </span>
            )},
        }),
        colHelper.display({
            header: "Category",
            id: "category",
            cell: (info) => (
                <span className="flex justify-center relative min-w-48">
                    <CategorySelect
                        register={register}
                        registerName={`category-${info.row.original.id}`}
                        setValue={setValue}
                        initialValue={info.row.original.categoryId}
                        label={undefined}
                    />
                </span>
            ),
        }),
        colHelper.display({
            header: "Actions",
            cell: (info) => {
                const part = info.row.original;

                return (
                    <div className="flex items-center space-x-4">
                        {/* <PencilIcon
                            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                            onClick={() => navigate(`/parts/${part.id}`)}
                        /> */}
                        <CheckCircleIcon
                            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                            onClick={() => {
                                console.log("Accepting category for part:", part);
                                handleSave(part);
                            }}
                        />
                        <ArchiveBoxIcon
                            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                            onClick={() => {
                                setToArchive([part.id]);
                                setArchiveOpen(true);
                            }}
                        />
                        <EyeIcon
                            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                            onClick={() => {
                                setMetadataPart(part);
                                setMetadataOpen(true);
                            }}
                        />
                        {/* <TrashIcon
                            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                            onClick={() => { setDeleteId(part.id); setDeleteOpen(true); }}
                        /> */}
                    </div>
                );
            },
        }),
    ];

    const handleSetPart = (part: IEbayPart) => {
        setPart(part);
    };

    const handleBulkSetCategory = (parts: IEbayPart[]) => {
        if (selectedCategory === undefined || selectedCategory === null) {
            console.error("No category selected for bulk set category action");
            return;
        }

        const assignData : any = {
            categoryId: selectedCategory.id,
            categoryName: selectedCategory.name,
            parts: parts?.map((part) => +part.id) || [],
            saveSearchTerm: saveSearchTerm,
            include: include ? include.trim() : null,
            exclude: exclude ? exclude.trim() : null,
        };

        assignCategory.mutate(assignData, {
            onSuccess: () => {
                console.log("Category assigned successfully");
                if (saveSearchTerm) {
                    queryClient.invalidateQueries({ queryKey: ["categorySearchTerms"] });
                }

            },
            onError: (error) => {
                console.error("Error assigning category:", error);
            },
        });
    };

    useEffect(() => {
        if (bulkCategoryOpen) return;
        setSelectedParts([]);
    }, [bulkCategoryOpen])

    const includeWatch          = watch("include");
    const excludeWatch          = watch("exclude");
    const externalCategoryWatch = watch("externalCategoryName");
    const categorySearchWatch = watch("categorySearchCategory");

    useEffect(() => {
        if (includeWatch !== undefined) {
            setInclude(includeWatch);
        }
    }, [includeWatch]);

    useEffect(() => {
        if (excludeWatch !== undefined) {
            setExclude(excludeWatch);
        }
    }, [excludeWatch]);

    useEffect(() => {
        if (externalCategoryWatch !== undefined) {
            setExternalCategory(externalCategoryWatch);
        }
    }, [externalCategoryWatch]);

    useEffect(() => {
        if (categorySearchWatch !== undefined) {
            setCategorySearch(categorySearchWatch?.name ?? "");
        }
    }, [categorySearchWatch]);

    const selectedCategory = watch("category");

    // --------- totals ---------
    const [totals, setTotals] = useState<number[]>([]);

    useEffect(() => {
        if (allTotals.data && !allTotals.isLoading) {
            setTotals([
                allTotals.data.uncategorised || 0,
                allTotals.data.categorised || 0,
                allTotals.data.accepted || 0,   
                allTotals.data.archived || 0,
            ]);
        }
    }, [allTotals.data, allTotals.isLoading]);

    const tabs = [
        {
            name: "Uncategorised",
            onClick: () => {
                setHideCategorised(true);
                setHideAccepted(true);
                setSelectedTab(0);
            },
        },
        {
            name: "Categorised",
            onClick: () => {
                setHideCategorised(false);
                setHideAccepted(true);
                setSelectedTab(1);
            },
        },
        {
            name: "Accepted",
            onClick: () => {
                setHideCategorised(false);
                setHideAccepted(false);
                setSelectedTab(2);
            },
        },
        {
            name: "Archived",
            onClick: () => {
                setHideCategorised(true);
                setHideAccepted(false);
                setSelectedTab(3);
            },
        },
    ]

    return (
        <div className="flex flex-col space-y-4">
            <ConfirmModal
                open={archiveOpen}
                setOpen={setArchiveOpen}
                title="Archive Part(s)"
                message="Archived parts can still be accessed in the archive section. Are you sure you want to archive this part?"
                confirmButtonText={"Archive Part(s)"}
                onConfirm={handleArchive}
                isLoading={false}
            />
            <div className="flex items-center justify-between">
                <div className="flex rounded-full fit-content">
                    {tabs.map((tab, index) => (
                        <div
                            className={`rounded-full py-2 px-8 shadow-md mr-[-35px] transition-colors duration-200
                            text-white text-sm cursor-pointer w-64 text-center
                            ${selectedTab == index ? "bg-orange-600" : "bg-slate-800 hover:bg-slate-700"}`}
                            onClick={tab.onClick}
                        >
                            {tab.name} ({totals[index] || 0})
                        </div>
                    ))}
                </div>

                <ButtonInput
                    classes="!w-48"
                    label="View Search Terms"
                    onClick={() => {
                        navigate("/scrape/search-terms");
                    }}
                    isSubmit={false}
                />
            </div>

            <div className="text-sm text-gray-700">
                Includes
            </div>
            <div className="relative flex items-stretch focus-within:z-10 w-full !mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                    />
                </div>
                <input
                    type="text"
                    className="w-full pl-10 py-2 border sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
                    placeholder={"Include by title"}
                    {...register("include")}
                />
            </div>

            <div className="text-sm text-gray-700">
                Excludes
            </div>
            <div className="relative flex items-stretch focus-within:z-10 w-full !mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                    />
                </div>
                <input
                    type="text"
                    className="w-full pl-10 py-2 border sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
                    placeholder={"Exclude by title"}
                    {...register("exclude")}
                />
            </div>

            
           

            <div className="flex items-center justify-start space-x-4">

                <div className="flex-grow">
                    <div className="text-sm text-gray-700">
                        External Category Name (Item Specifics)
                    </div>
                    <div className="relative flex items-stretch focus-within:z-10 w-full !mt-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                            />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 py-2 border sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
                            placeholder={"External Category Name"}
                            {...register("externalCategoryName")}
                        />
                    </div>
                </div>

                <div className="flex-grow">
                    <div className="text-sm text-gray-700">
                        Category Search
                    </div>
                    <div className="!mt-1">
                        <CategorySelect
                            register={register}
                            registerName="categorySearchCategory"
                            setValue={setValue}
                            label={undefined}
                        />
                    </div>
                </div>

                <div className="flex-grow">
                    <CategorySelect register={register} setValue={setValue} label={"Category"} />
                </div>

                <div className="relative flex items-stretch  focus-within:z-10 mt-[21px]">
                   <div className="classes">
                        <Field as="div" className={"flex items-center"}>
                            <span className="mr-10">
                                <Label as="span" className="text-md text-gray-900" passive>
                                    Save Search Term
                                </Label>
                            </span>
                            <Switch
                                // {...register(registerName)}
                                checked={saveSearchTerm}
                                onChange={(e) => setSaveSearchTerm(e)}
                                className={classNames(
                                    saveSearchTerm ? 'bg-orange-600' : 'bg-gray-200',
                                    'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-300'
                                )}
                            >
                                <span
                                    aria-hidden="true"
                                    className={classNames(
                                    saveSearchTerm ? 'translate-x-5' : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                                    )}
                                />
                            </Switch>
                        </Field>
                    </div>
                </div>       
            </div>

            <div className="flex items-center justify-center border-t-2 border-gray-200 h-[0px] !mt-6" >
                <div className="bg-gray-100 px-6 pb-1">or</div>
            </div>

            <div className="flex items-center justify-start space-x-4">
                <div className="flex-grow">
                    <CategorySearchTermSelect
                        register={register}
                        registerName="categorySearchTerm"
                        setValue={(registerName: string, value: any) => {
                            setSelectedSearchTerm(value);
                            setValue("include", value?.include || "");
                            setValue("exclude", value?.exclude || "");
                        }}
                        label={"Select Search Term"}
                    />
                </div>

            </div>

            <Table
                url="/part"
                queryKey={PartQueryKeys.all(hideCategorised, hideAccepted)}
                columns={cols}
                // searchLabel="Search by vin or part number"
                externalFilters={{
                    hideCategorised: hideCategorised,
                    hideAccepted: hideAccepted,
                    include: include,
                    exclude: exclude,
                    externalCategoryName: externalCategory,
                    categorySearch: categorySearch,
                }}
                bulkActions={[
                    {
                        label: "Set Category",
                        onClick: handleBulkSetCategory,
                    },
                    {
                        label: "Archive",
                        onClick: (parts: IEbayPart[]) => {
                            setToArchive(parts.map((p) => p.id));
                            setArchiveOpen(true);
                        },
                    }
                ]}
            />

            <Modal open={metadataOpen} setOpen={setMetadataOpen} width="sm:max-w-4xl">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-semibold">Part metadata</h1>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Title</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.title || "—"}</p>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Item Specifics</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.itemSpecifics || "—"}</p>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Quick Specs</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.quickspecs || "—"}</p>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Vehicle Specifications</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.vehicleSpecifications || "—"}</p>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Part Info</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.partInfo || "—"}</p>
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700">Part Comment</h2>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.partComment || "—"}</p>
                        </div>
                    </div>
                </div>
            </Modal>

            <PartsCategoryModal
                open={bulkCategoryOpen}
                setOpen={setBulkCategoryOpen}
                parts={selectedParts}

                saveSearchTerm={saveSearchTerm}
                include={include}
                exclude={exclude}
            />
        </div>
    );
}